import { useCallback } from 'react';
import { useStore } from '../store/store';

export const useExtendedCalorieCalculation = () => {
  const {
    gender,
    born,
    birthDate,
    height,
    weight,
    idealWeight,
    workOut
  } = useStore();

  const calculateExtendedCalories = useCallback((): number => {
    const FALLBACK_CALORIES = 1500;

    if (!gender || !height || !weight) {
      return FALLBACK_CALORIES;
    }

    let age: number = 0;
    
    if (born) {
      age = parseInt(born as string) || 0;
    } else if (birthDate) {
      const birthDateObj = birthDate as { year: string; month: string; day: string };
      const birth = new Date(
        parseInt(birthDateObj.year),
        parseInt(birthDateObj.month) - 1,
        parseInt(birthDateObj.day)
      );
      const today = new Date();
      age = today.getFullYear() - birth.getFullYear();
      
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
    } else {
      return FALLBACK_CALORIES;
    }

    let heightInCm: number;
    
    if (height.type === "cm" && height.cm) {
      heightInCm = parseFloat(height.cm);
    } else if (height.type === "ft" && height.ft) {
      const feet = parseFloat(height.ft);
      heightInCm = feet * 30.48;
    } else {
      return FALLBACK_CALORIES;
    }

    let weightInKg: number;
    
    if (weight.type === "kg" && weight.kg) {
      weightInKg = parseFloat(weight.kg);
    } else if (weight.type === "lbs" && weight.lbs) {
      const pounds = parseFloat(weight.lbs);
      weightInKg = pounds * 0.453592;
    } else {
      return FALLBACK_CALORIES;
    }

    if (isNaN(age) || isNaN(heightInCm) || isNaN(weightInKg) || age <= 0 || heightInCm <= 0 || weightInKg <= 0) {
      return FALLBACK_CALORIES;
    }

    const baseBMR = gender === "Male" 
      ? (10 * weightInKg + 6.25 * heightInCm - 5 * age + 5)
      : (10 * weightInKg + 6.25 * heightInCm - 5 * age - 161);

    const getActivityCoefficient = (): number => {
      if (!workOut) return 1.25;
      
      const workoutLower = workOut.toLowerCase();
      
      if (workoutLower.includes('low') || workoutLower.includes('sedentary')) {
        return 1.25;
      } else if (workoutLower.includes('light') || workoutLower.includes('lightly')) {
        return 1.375;
      } else if (workoutLower.includes('active') || workoutLower.includes('moderate')) {
        return 1.55;
      } else if (workoutLower.includes('very') || workoutLower.includes('high')) {
        return 1.725;
      }
      
      return 1.25;
    };

    const activityCoefficient = getActivityCoefficient();

    const getWeightDynamicsCoefficient = (): number => {
      if (!idealWeight || !idealWeight.value) return 1.0;
      
      let idealWeightInKg: number;
      
      if (idealWeight.type === "kg" && idealWeight.value) {
        idealWeightInKg = parseFloat(idealWeight.value);
      } else if (idealWeight.type === "lbs" && idealWeight.value) {
        idealWeightInKg = parseFloat(idealWeight.value) * 0.453592;
      } else {
        return 1.0;
      }

      if (isNaN(idealWeightInKg) || idealWeightInKg <= 0) return 1.0;

      const weightDifference = ((weightInKg - idealWeightInKg) / weightInKg) * 100;

      if (weightDifference > 5) {
        return 0.8;
      } else if (weightDifference < -5) {
        return 1.2;
      } else {
        return 1.0;
      }
    };

    const weightDynamicsCoefficient = getWeightDynamicsCoefficient();

    const finalCalories = baseBMR * activityCoefficient * weightDynamicsCoefficient;

    return Math.round(finalCalories);
  }, [gender, born, birthDate, height, weight, idealWeight, workOut]);

  return calculateExtendedCalories;
};